import { useState } from 'react';
import { Button } from '@clay-run/glaze/button';
import { SelectField } from '@clay-run/glaze/select-field';
import { TextareaField } from '@clay-run/glaze/textarea-field';
import { useDraftState } from '../../../hooks/useDraftState';
import { useArchiveSignal } from './useSignalsMutations';
import { useArchiveReasons } from './useArchiveReasons';
import { AUTOFILL_IGNORE_PROPS } from '../../../lib/autofill';

/**
 * The Signal card's INLINE archive form (QA batch 2 unit B8, D-I) — the
 * moved body of the deleted `ArchiveDialog` (select + textarea + warning
 * copy + R26 prefill-as-option logic + inline error box), with the
 * `AlertDialog` shell gone: no modals anywhere in Signals. Parity with
 * Action View's own inline form (`mini-apps/action-view/components/ActionsColumn.tsx:38-100`,
 * read, never imported).
 *
 * Draft State Under SSE (CLAUDE.md, `docs/v4-system/draft-state-under-sse.md`):
 * the card is mounted inside the v4 tree and re-renders on every
 * `artifact:updated` event. So the live draft is `useDraftState(seed,
 * signalId)`: keyed by the SELECTED SIGNAL ID, a same-id re-render (a fresh
 * `archivePrefill` or `initialDraft` object) never reseeds and never
 * clobbers typing, and a new id reseeds from that signal's own seed
 * (`terra/no-effect-reseeded-draft` — no reseed effect).
 *
 * Seed precedence: the card's RETAINED draft for this signal
 * (`initialDraft`) → the signal's `archivePrefill`. The form unmounts
 * whenever the card closes it (selector switch, Cancel), which would drop
 * its local state; so every edit is also written through `onDraftChange`
 * to `useSignalActionCard.archiveDrafts`, and a reopened form seeds from
 * it (fix-plan-batch5 D1, QA item 80). While mounted, `useDraftState` stays
 * the one live copy — the retained map is only read at (re)mount.
 */

export type ArchiveDraft = { reason: string; feedback: string };

export interface SignalActionArchiveFormProps {
  /** The signal this form archives — also the draft's entity key. */
  signalId: string;
  /** From `getSignalRowActions(signal, now).archivePrefill` — the single matrix (D-A). */
  archivePrefill: { reason?: string; feedback?: string };
  /** Cancel, or a successful archive — the card closes the form. */
  onDone: () => void;
  /** Fires on a successful archive, before `onDone` — drives auto-expand-next. */
  onActed: (signalId: string) => void;
  /** The signal's `signal_type`, for the `Action Archived` analytics event. */
  signalType: string | null;
  /** The card's retained draft for `signalId` — wins over `archivePrefill` as the seed. */
  initialDraft?: ArchiveDraft;
  /** Called with the full next draft on every edit — the card retains it across form remounts. */
  onDraftChange?: (draft: ArchiveDraft) => void;
}

export function SignalActionArchiveForm({
  signalId,
  archivePrefill,
  onDone,
  onActed,
  signalType,
  initialDraft,
  onDraftChange,
}: SignalActionArchiveFormProps) {
  const prefillSeed: ArchiveDraft = { reason: archivePrefill.reason ?? '', feedback: archivePrefill.feedback ?? '' };
  const seed = initialDraft ?? prefillSeed;
  const form = useDraftState<ArchiveDraft>(seed, signalId);
  const { reason, feedback } = form.draft ?? seed;
  function updateDraft(patch: Partial<ArchiveDraft>) {
    form.setDraft(patch);
    onDraftChange?.({ reason, feedback, ...patch });
  }
  // Keyed by signal id like the draft — an error for one signal never
  // follows the rep to the next one they pick in the selector.
  const [submitError, setSubmitError] = useState<
    { signalId: string; title: string; detail: string | undefined; oauthUrl: string | undefined } | null
  >(null);
  const visibleError = submitError?.signalId === signalId ? submitError : null;

  const reasonsQuery = useArchiveReasons();
  // `onActed` rides the HOOK's `onSuccess` (round-1 fix D-J), never a
  // per-`mutate()` callback — the list refetch this mutation triggers can
  // unmount the card that owns this form, and TanStack v5 skips per-mutate
  // callbacks once that observer is gone.
  const archive = useArchiveSignal({ onActed });

  const reasonOptions = reasonsQuery.data?.values ?? [];
  /**
   * R26 (moved verbatim from `ArchiveDialog`) — the prefill (e.g.
   * `'Removed from Flow'`) is code-owned; the options are the LIVE
   * Salesforce picklist. When they disagree the prefill is appended as a
   * real item, so "what you see" and "what gets submitted" stay identical —
   * in EVERY query state (round-2 fix S3/D-S): while loading or failed the
   * picklist is `[]` and the prefill is still the visible, selected value.
   */
  const prefillNeedsOwnOption = reason.length > 0 && !reasonOptions.some(r => r.value === reason);
  const reasonItems = [
    ...reasonOptions.map(r => ({ value: r.value, label: r.label })),
    ...(prefillNeedsOwnOption ? [{ value: reason, label: reason }] : []),
  ];
  const canSubmit = reason.trim().length > 0 && feedback.trim().length > 0 && !archive.isPending;

  function handleSubmit() {
    setSubmitError(null);
    archive.mutate(
      { signalId, reason, feedback, signalType },
      {
        onSuccess: () => {
          form.reseed(prefillSeed);
          onDone();
        },
        // `err.title`/`err.detail` are the server's own `problem()` sentence
        // (or the shared network sentence) — never a status code (CLAUDE.md).
        // `.title` is optional on `ApiProblemError` (unset for network/
        // aborted) — fall back to `.message` (plan U4a).
        onError: err =>
          setSubmitError({ signalId, title: err.title ?? err.message, detail: err.detail, oauthUrl: err.oauthUrl }),
      },
    );
  }

  return (
    <div className="grid gap-2.5">
      <p className="text-[13px] text-content-secondary dark:text-nightshade-300">
        Archiving closes this task in Salesforce and removes the contact from the Gong flow. This can&rsquo;t be undone.
      </p>

      <SelectField<string | null>
        label="Archive Reason"
        aria-label="Archive Reason"
        items={reasonItems}
        value={reason === '' ? null : reason}
        onValueChange={(value: string | null) => updateDraft({ reason: value ?? '' })}
        placeholder={reasonsQuery.isLoading ? 'Loading reasons…' : 'Select a reason…'}
        disabled={reasonsQuery.isLoading}
        errorMessage={
          reasonsQuery.isError
            ? reasonsQuery.error instanceof Error
              ? reasonsQuery.error.message
              : "Couldn't load archive reasons."
            : undefined
        }
        size="sm"
      />

      <TextareaField
        {...AUTOFILL_IGNORE_PROPS}
        label="Feedback"
        aria-label="Feedback"
        rows={3}
        value={feedback}
        onValueChange={(value: string) => updateDraft({ feedback: value })}
        placeholder="e.g., too below the line, enrichment error, already in contact, good lead"
        size="sm"
      />

      {visibleError && (
        <div data-testid="archive-submit-error" className="text-[12.5px] text-content-danger dark:text-red-400">
          <div className="font-medium">{visibleError.title}</div>
          {visibleError.detail && <div>{visibleError.detail}</div>}
          {visibleError.oauthUrl && (
            <a href={visibleError.oauthUrl} className="font-semibold underline">
              Reconnect Salesforce
            </a>
          )}
        </div>
      )}

      <div className="flex items-center justify-end gap-1.5">
        <Button type="button" size="sm" variant="secondary" onClick={onDone}>
          Cancel
        </Button>
        <Button type="button" size="sm" variant="danger" loading={archive.isPending} disabled={!canSubmit} onClick={handleSubmit}>
          Archive
        </Button>
      </div>
    </div>
  );
}
